import type { FC, SyntheticEvent } from 'react'
import { Modal, SimpleButton } from '@opallabs/lapidary'
import * as styles from './styles'

const stopPropagation = (e: SyntheticEvent) => e.stopPropagation()

/**
 * Confirms a switch of a People Picker value to the `off` notification mode.
 * This switch drops the pending notifications of the value.
 *
 * Render this modal beside the picker, not inside `NotificationFooter`. The
 * footer exists only while the picker menu is open.
 *
 * The mode-menu item that opens this modal is gone by the time it closes, so
 * focus would fall to the page body. Pass `onCloseAutoFocus` to move it back.
 */
export const ConfirmNotificationsOffModal: FC<{
  onCancel: () => void
  onCloseAutoFocus?: () => void
  onConfirm: () => void
}> = ({ onCancel, onCloseAutoFocus, onConfirm }) => (
  // This div acts as an event fence for the modal since this modal appears
  // while two other radix UI dropdowns are also listening for events.
  <div
    css={{ display: 'contents' }}
    onMouseDown={stopPropagation}
    onMouseUp={stopPropagation}
    onPointerDown={stopPropagation}
    onPointerUp={stopPropagation}
    role="presentation"
  >
    <Modal.Root defaultOpen>
      <Modal.Content
        css={styles.disableNotificationsModal}
        onCloseAutoFocus={
          onCloseAutoFocus &&
          ((e) => {
            e.preventDefault()
            onCloseAutoFocus()
          })
        }
        onEscapeKeyDown={onCancel}
        overlayStyles={styles.disableNotificationsOverlay}
      >
        <Modal.Title>Disable User Notifications</Modal.Title>
        <Modal.Body>
          <Modal.Description>
            Are you sure you want to disable user notifications? Currently
            pending notifications will not be sent. You may enable notifications
            at any time, but unsent notifications will not be able to be
            re-triggered.
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <SimpleButton
            name="cancel-disable-notifications"
            onClick={onCancel}
            variant="ghost-white"
          >
            Cancel
          </SimpleButton>
          <SimpleButton
            name="confirm-disable-notifications"
            onClick={onConfirm}
          >
            Save
          </SimpleButton>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  </div>
)
ConfirmNotificationsOffModal.displayName =
  'Satellite: ConfirmNotificationsOffModal ← CustomFields'
